import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError as observableThrowError} from 'rxjs';
import {catchError, last} from 'rxjs/operators';
import {MessageType} from '../../shared/constants/message-type.constant';
import {MessageService} from '../../shared/services/message.service';
import {Message} from '../../shared/models/message.model';
import {AuthService} from '../auth/auth.service';
import {ClientAccountService} from '../../shared/services/client-account.service';
import {UsersService} from '../../shared/services/users.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  errorMap = {
    103: {
      title: 'Risorsa non trovata',
      message:
        'Non ci sono risultati per la risorsa',
    },
    409: {
      title: 'Impossibile eseguire l\'operazione',
      type: MessageType.ERROR_MANAGED,
    },
  };

  constructor(
    private errorService: MessageService,
    private authService: AuthService,
    private clientAccountService: ClientAccountService,
    private usersService: UsersService
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      last(),
      catchError((err: any, caught) => {
        console.log(err);
        // Caso di errore generico
        if (err instanceof HttpErrorResponse) {
          // inizializzo il messaggio d'errore
          console.log('###', err.status, err);
          let errorMex: Message;
          switch (err.status) {
            // caso non c'è internet
            case 0:
              errorMex = new Message();
              errorMex.code = 0;
              errorMex.type = MessageType.ERROR_MANAGED;
              errorMex.message = 'NETWORK_ERROR';

              this.errorService.throwMessage(errorMex);
              break;
            case 400:
              errorMex = new Message();
              errorMex.code = err.error[0]?.code;
              errorMex.type = MessageType.ERROR_MANAGED;
              errorMex.title = 'VALIDATION_ERROR';
              errorMex.message = '';
              if (errorMex.code == 307) {
                errorMex.message = "VALIDATION_ERROR_MESSAGE";
              }
              if (errorMex.code == 315) {
                errorMex.message = "IN_DEVELOP";
                errorMex.type = MessageType.WARNING_MESSAGE;
              }
              else {
                if (err.error) {
                  var loggedMessage = '';
                  var shownMessage = '';
                  for (let e of err.error) {
                    loggedMessage = loggedMessage + e.message + '\n';
                    if(e.hasOwnProperty('externalErrors') && e.externalErrors.length>0) {
                      for (let d of e.externalErrors) {
                        loggedMessage = loggedMessage + d.message + '\n';
                        shownMessage = shownMessage + d.message + '\n';
                      }
                    }else{
                      shownMessage = shownMessage + e.message + '\n';
                    }
                  }
                  console.log('ERROR: ' + errorMex.message + '\n' + loggedMessage);
                  errorMex.message = errorMex.message + '\n' + shownMessage;
                }
              }
              this.errorService.throwMessage(errorMex);
              break;
            // problemi con autenticazione o autorizzazione
            case 401:
              if (err.error === null) {
                errorMex = new Message();
                errorMex.code = 0;
                errorMex.type = MessageType.ERROR_MANAGED;
                errorMex.message = 'NETWORK_ERROR';
                this.errorService.throwMessage(errorMex);
                break;
              }
              if (err.error[0].code === '002') {
                errorMex = new Message();
                errorMex.type = MessageType.ERROR_MANAGED;
                errorMex.message = 'WRONG_CREDENTIALS';
                this.errorService.throwMessage(errorMex);
                break;
              }
              this.clientAccountService.clearUnit(true);
              this.usersService.clearUser();
              this.clientAccountService.clearUnit();
              this.authService.logout();
              break;
            // Forbidden o Site disabled
            case 403:
              const managedErrors = {
                200: "FORBIDDEN_CUSTOMER",
                201: "FORBIDDEN_ACCOUNT",
                205: "FORBIDDEN_OPERATION",
                206: "PERMISSION_EXPIRED",
              }
              const [{code}] = err.error;
              const message = managedErrors[code];
              if (message) {
                errorMex = new Message();
                errorMex.type = MessageType.ERROR_MANAGED;
                errorMex.message = message;
                this.errorService.throwMessage(errorMex);
                break;
              }
              this.clientAccountService.clearUnit(true);
              this.usersService.clearUser();
              this.clientAccountService.clearUnit();
              this.authService.logout();
              break;
            case 404:
              if (err.error[0].code === '114') { // identità non trovata
                break;
              }
              errorMex = new Message();
              errorMex.type = MessageType.ERROR_MANAGED;
              errorMex.message = 'WRONG_RESOURCE_OR_NO_RESULTS';
              this.errorService.throwMessage(errorMex);
              break;
            case 409:
              if (err.error[0].code === '209') {
                errorMex = new Message();
                errorMex.type = MessageType.ERROR_MANAGED;
                errorMex.message = 'CERT_EMAIL_CONFLICT';
                break;
              }
              errorMex = new Message();
              errorMex.type = MessageType.ERROR_MANAGED;
              errorMex.message = 'MOMENTARY_ERROR';
              this.errorService.throwMessage(errorMex);
              break;
            // errore imprevisto (internal server error)
            case 500:
              if (req.url.includes('import/csv')) {
                errorMex = new Message();
                errorMex.type = MessageType.ERROR_MANAGED;
                errorMex.message = 'CSV_WRONG_FORMAT';
                this.errorService.throwMessage(errorMex);
                break;
              }
              errorMex = new Message();
              errorMex.type = MessageType.ERROR_MANAGED;
              errorMex.message = 'MOMENTARY_ERROR';
              this.errorService.throwMessage(errorMex);
              break;
            // errore imprevisto (internal server error)
            case 503:
              errorMex = new Message();
              errorMex.type = MessageType.ERROR_MANAGED;
              errorMex.message = 'UNREACHABLE_ERROR';
              this.errorService.throwMessage(errorMex);
              break;
            default:
              errorMex = this.mappaMessaggioErrore(err);
              this.errorService.throwMessage(errorMex);
              break;
          }

          /*
           * qui lo restituisco gestito
           */
          if (!errorMex) {
            return observableThrowError(this.mappaMessaggioErrore(err));
          }
          return observableThrowError(errorMex);
        } else {
          let errorMex = this.mappaMessaggioErrore(err);
          this.errorService.throwMessage(errorMex);
        }
      }),
    );
  }

  mappaMessaggioErrore(response: HttpErrorResponse): Message {
    const errore: Message = new Message();
    errore.type = MessageType.ERROR_BLOCK;

    // estraggo l'errore dalla response
    const erroreInput: Message = response.error as Message;

    // se è veramente un tipo CdcErrorMessage
    if ('statusCode' in response) {
      const info = this.errorMap[erroreInput.code];
      if (info) {
        errore.message = info.title;
        if (info.type) {
          errore.type = info.type;
        }
      }
      console.log(errore);
      return errore;
    } else {
      // se è un errore non gestito (non applicativo)
      errore.message = response.message;
      return errore;
    }
  }

  isJsonObject(obj: any): boolean {
    try {
      const json = JSON.stringify(obj);
      return json.charAt(0) === '{';
    } catch {
      return false;
    }
  }
}

export const HttpErrorInterceptorProvider: any = {
  provide: HTTP_INTERCEPTORS,
  useClass: HttpErrorInterceptor,
  multi: true,
};
